import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import styled from 'styled-components';
import { colors } from '../../const';
import { Typography } from '../typography';
import { Card, CardHeader } from './Components';

export type MapPanelLocation = {
  lng: number;
  lat: number;
  value: number;
  name?: string;
};

export interface MapPanelProps {
  title: string;
  locations: MapPanelLocation[];
  legendTitle: string;
  /** Верхняя граница шкалы легенды (подпись «250+») */
  valueMax: number;
}

/** Демо-данные как на макете overview */
export const MAP_PANEL_DEMO_LOCATIONS: MapPanelLocation[] = [
  { name: 'North America', lng: -98, lat: 39, value: 128 },
  { name: 'Europe', lng: 10, lat: 50, value: 96 },
  { name: 'South Asia', lng: 78, lat: 22, value: 231 },
  { name: 'South America', lng: -58, lat: -15, value: 45 },
  { name: 'Africa', lng: 28, lat: -26, value: 38 },
  { name: 'Australia', lng: 134, lat: -25, value: 62 },
];

const WORLD_MAP_NAME = 'world';
const DEFAULT_VALUE_MAX = 250;
const DEFAULT_GEO_ZOOM = 1.15;
const GEO_ZOOM_STEP = 1.25;
const GEO_MIN_ZOOM = 0.85;
const GEO_MAX_ZOOM = 9;
const GEO_CENTER: [number, number] = [0, 18];
const GEO_SCALE_LIMIT = { min: GEO_MIN_ZOOM, max: GEO_MAX_ZOOM };
/** Допустимое смещение center от дефолта: при min zoom — узко, при max zoom — шире */
const PAN_LNG_HALF_AT_MIN_ZOOM = 70;
const PAN_LNG_HALF_AT_MAX_ZOOM = 175;
const PAN_LAT_HALF_AT_MIN_ZOOM = 30;
const PAN_LAT_HALF_AT_MAX_ZOOM = 60;

type GeoDragState = {
  pointerX: number;
  pointerY: number;
  center: [number, number];
  zoom: number;
};

type ZrPointerEvent = {
  offsetX: number;
  offsetY: number;
  event: MouseEvent;
};

type MapTooltipState = {
  x: number;
  y: number;
  name: string;
  value: number;
};

const ChartWrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;

  .echarts-for-react,
  .echarts-for-react > div {
    width: 100% !important;
    height: 100% !important;
    cursor: grab !important;
  }

  .echarts-for-react.is-map-dragging,
  .echarts-for-react.is-map-dragging > div {
    cursor: grabbing !important;
  }
`;

const LegendOverlay = styled.div`
  position: absolute;
  left: 0.4rem;
  bottom: 0.4rem;
  display: grid;
  row-gap: 0.6rem;
  z-index: 1;
  pointer-events: none;
`;

const GradientBar = styled.div`
  width: 12rem;
  height: 1.5rem;
  background: linear-gradient(
    90deg,
    ${colors['--primary-30']} 0%,
    ${colors['--primary-60']} 45%,
    ${colors['--primary-100']} 100%
  );
`;

const GradientLabels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 12rem;
`;

const ZoomControls = styled.div`
  position: absolute;
  right: 0.4rem;
  bottom: 0.4rem;
  display: grid;
  z-index: 2;
  border: 0.1rem solid var(--gray-50);
  border-radius: var(--border-radius-4);
  overflow: hidden;
  background: var(--white);
  pointer-events: none;
`;

const ZoomButton = styled.button`
  width: 2.8rem;
  height: 2.8rem;
  border: none;
  background: var(--white);
  color: var(--gray-80);
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  pointer-events: auto;

  &:not(:last-child) {
    border-bottom: 0.1rem solid var(--gray-50);
  }

  &:hover {
    background: var(--gray-30);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const LocationTooltip = styled.div`
  position: absolute;
  z-index: 3;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 1rem));
  padding: 0.6rem 1rem;
  border: 0.1rem solid var(--gray-50);
  border-radius: var(--border-radius-4);
  background: var(--white);
  box-shadow: 0 0.8rem 2rem rgba(19, 21, 35, 0.12);
  white-space: nowrap;
`;

const getGeoConfig = (zoom: number, center: [number, number] = GEO_CENTER) => ({
  map: WORLD_MAP_NAME,
  /** zoom колёсиком — да; pan — наш обработчик на всём canvas (ZRender) */
  roam: 'scale' as const,
  zoom,
  center,
  scaleLimit: GEO_SCALE_LIMIT,
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  layoutCenter: ['50%', '50%'],
  layoutSize: '100%',
  itemStyle: {
    areaColor: colors['--gray-60'],
    borderColor: colors['--white'],
    borderWidth: 0.6,
  },
  emphasis: {
    disabled: true,
  },
  label: {
    show: false,
  },
  tooltip: {
    show: false,
  },
  silent: true,
});

const clampGeoZoom = (zoom: number) => Math.min(GEO_MAX_ZOOM, Math.max(GEO_MIN_ZOOM, zoom));

const getPanHalfRange = (zoom: number, minHalf: number, maxHalf: number) => {
  const span = GEO_MAX_ZOOM - GEO_MIN_ZOOM;
  const t = span > 0 ? (clampGeoZoom(zoom) - GEO_MIN_ZOOM) / span : 0;
  return minHalf + t * (maxHalf - minHalf);
};

const clampGeoCenter = (center: [number, number], zoom: number): [number, number] => {
  const lngHalf = getPanHalfRange(zoom, PAN_LNG_HALF_AT_MIN_ZOOM, PAN_LNG_HALF_AT_MAX_ZOOM);
  const latHalf = getPanHalfRange(zoom, PAN_LAT_HALF_AT_MIN_ZOOM, PAN_LAT_HALF_AT_MAX_ZOOM);

  return [
    Math.min(GEO_CENTER[0] + lngHalf, Math.max(GEO_CENTER[0] - lngHalf, center[0])),
    Math.min(GEO_CENTER[1] + latHalf, Math.max(GEO_CENTER[1] - latHalf, center[1])),
  ];
};

const isGeoViewChanged = (
  zoom: number,
  center: [number, number],
  clampedZoom: number,
  clampedCenter: [number, number],
) =>
  Math.abs(zoom - clampedZoom) > 0.0001 ||
  Math.abs(center[0] - clampedCenter[0]) > 0.0001 ||
  Math.abs(center[1] - clampedCenter[1]) > 0.0001;

const isChartAlive = (chart: echarts.ECharts | null | undefined): chart is echarts.ECharts =>
  Boolean(chart && typeof chart.isDisposed === 'function' && !chart.isDisposed());

const readGeoZoomFromChart = (chart: echarts.ECharts): number => {
  const geoRaw = chart.getOption().geo;
  const geo = Array.isArray(geoRaw) ? geoRaw[0] : geoRaw;
  const zoom = geo?.zoom;
  return typeof zoom === 'number' ? zoom : DEFAULT_GEO_ZOOM;
};

const readGeoCenterFromChart = (chart: echarts.ECharts): [number, number] => {
  const geoRaw = chart.getOption().geo;
  const geo = Array.isArray(geoRaw) ? geoRaw[0] : geoRaw;
  const center = geo?.center;
  return Array.isArray(center) && center.length === 2 ? [center[0], center[1]] : GEO_CENTER;
};

const convertPixelToGeo = (
  chart: echarts.ECharts,
  x: number,
  y: number,
): [number, number] | null => {
  const coord = chart.convertFromPixel({ geoIndex: 0 }, [x, y]);
  if (!coord || coord.length < 2) return null;
  if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return null;
  return [coord[0], coord[1]];
};

const setChartDraggingClass = (chart: echarts.ECharts, isDragging: boolean) => {
  const dom = chart.getDom();
  dom.style.cursor = isDragging ? 'grabbing' : 'grab';
  dom.closest('.echarts-for-react')?.classList.toggle('is-map-dragging', isDragging);
};

const PAN_DRAG_THRESHOLD_PX = 5;
const TOOLTIP_HIT_PADDING = 8;
const LOCATION_SYMBOL_SIZE = 32;

const findHoveredLocation = (
  chart: echarts.ECharts,
  points: MapPanelLocation[],
  pointerX: number,
  pointerY: number,
): MapTooltipState | null => {
  let hovered: MapTooltipState | null = null;
  let minDistance = Infinity;

  points.forEach((point) => {
    const pixel = chart.convertToPixel({ geoIndex: 0 }, [point.lng, point.lat]);
    if (!Array.isArray(pixel) || pixel.length < 2) return;

    const [x, y] = pixel;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    const distance = Math.hypot(pointerX - x, pointerY - y);
    const hitRadius = LOCATION_SYMBOL_SIZE / 2 + TOOLTIP_HIT_PADDING;

    if (distance <= hitRadius && distance < minDistance) {
      minDistance = distance;
      hovered = {
        x,
        y,
        name: point.name ?? '',
        value: point.value,
      };
    }
  });

  return hovered;
};

const buildChartOption = (
  points: MapPanelLocation[],
  valueMax: number,
  geoZoom: number,
  geoCenter: [number, number],
): EChartsOption => ({
  animation: false,
  backgroundColor: 'transparent',
  geo: getGeoConfig(geoZoom, geoCenter),
  visualMap: {
    show: false,
    dimension: 2,
    min: 1,
    max: valueMax,
    inRange: {
      symbolSize: LOCATION_SYMBOL_SIZE,
      color: [colors['--primary-70'], colors['--primary-100']],
    },
  },
  series: [
    {
      type: 'effectScatter',
      coordinateSystem: 'geo',
      data: points.map((p) => ({
        name: p.name,
        value: [p.lng, p.lat, p.value],
      })),
      showEffectOn: 'render',
      rippleEffect: {
        brushType: 'stroke',
        scale: 3.2,
        period: 4,
        number: 2,
      },
      label: {
        show: true,
        formatter: ({ value }) => String((value as number[])[2] ?? ''),
        color: colors['--white'],
        fontSize: 11,
        fontWeight: 600,
      },
      itemStyle: {
        shadowBlur: 12,
        shadowColor: 'rgba(30, 94, 255, 0.35)',
      },
      emphasis: {
        scale: 1.06,
        focus: 'self',
      },
      silent: false,
      zlevel: 2,
    },
  ],
});

export const MapPanel = ({
  title,
  locations = MAP_PANEL_DEMO_LOCATIONS,
  legendTitle,
  valueMax: valueMaxProp,
}: MapPanelProps) => {
  const chartRef = useRef<echarts.ECharts | null>(null);
  const geoCenterRef = useRef<[number, number]>(GEO_CENTER);
  const geoZoomRef = useRef(DEFAULT_GEO_ZOOM);
  const geoDragRef = useRef<GeoDragState | null>(null);
  const panPendingRef = useRef<GeoDragState | null>(null);
  const unbindCanvasPanRef = useRef<(() => void) | null>(null);
  const geoRoamFrameRef = useRef<number | null>(null);
  const geoRoamSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mapReady, setMapReady] = useState(() => Boolean(echarts.getMap(WORLD_MAP_NAME)));
  /** Только для disabled-состояния кнопок; не участвует в option (избегаем dispose при wheel-zoom) */
  const [geoZoomUi, setGeoZoomUi] = useState(DEFAULT_GEO_ZOOM);
  const [tooltip, setTooltip] = useState<MapTooltipState | null>(null);

  const safeLocations = useMemo(
    () =>
      (locations ?? []).filter(
        (p) =>
          Number.isFinite(p.lng) &&
          Number.isFinite(p.lat) &&
          Number.isFinite(p.value) &&
          p.value > 0,
      ),
    [locations],
  );

  const valueMax = useMemo(() => {
    const dataMax = safeLocations.reduce((max, p) => Math.max(max, p.value), 0);
    return Math.max(valueMaxProp ?? DEFAULT_VALUE_MAX, dataMax, 1);
  }, [safeLocations, valueMaxProp]);

  useEffect(() => {
    if (echarts.getMap(WORLD_MAP_NAME)) {
      setMapReady(true);
      return;
    }

    let cancelled = false;

    const loadWorldMap = async () => {
      try {
        const base = process.env.PUBLIC_URL ?? '';
        const response = await fetch(`${base}/geo/world.json`);
        if (!response.ok) {
          throw new Error(`world.json: ${response.status}`);
        }
        const geoJson = await response.json();
        if (!cancelled) {
          echarts.registerMap(WORLD_MAP_NAME, geoJson);
          setMapReady(true);
        }
      } catch {
        if (!cancelled) {
          setMapReady(false);
        }
      }
    };

    void loadWorldMap();

    return () => {
      cancelled = true;
    };
  }, []);

  const option = useMemo(
    () => buildChartOption(safeLocations, valueMax, DEFAULT_GEO_ZOOM, GEO_CENTER),
    [safeLocations, valueMax],
  );

  const scheduleGeoZoomUiSync = useCallback(() => {
    if (geoRoamSyncTimeoutRef.current) {
      clearTimeout(geoRoamSyncTimeoutRef.current);
    }

    geoRoamSyncTimeoutRef.current = setTimeout(() => {
      geoRoamSyncTimeoutRef.current = null;
      setGeoZoomUi((prev) =>
        Math.abs(prev - geoZoomRef.current) < 0.0001 ? prev : geoZoomRef.current,
      );
    }, 80);
  }, []);

  const applyChartGeoView = useCallback((chart: echarts.ECharts, zoom: number) => {
    if (!isChartAlive(chart)) return;

    const center = readGeoCenterFromChart(chart);
    const clampedZoom = clampGeoZoom(zoom);
    const clampedCenter = clampGeoCenter(center, clampedZoom);

    geoCenterRef.current = clampedCenter;
    geoZoomRef.current = clampedZoom;

    chart.setOption({
      geo: getGeoConfig(clampedZoom, clampedCenter),
    });

    setGeoZoomUi(clampedZoom);
  }, []);

  const zoomIn = useCallback(() => {
    const chart = chartRef.current;
    if (!isChartAlive(chart)) return;

    applyChartGeoView(chart, geoZoomRef.current * GEO_ZOOM_STEP);
  }, [applyChartGeoView]);

  const zoomOut = useCallback(() => {
    const chart = chartRef.current;
    if (!isChartAlive(chart)) return;

    applyChartGeoView(chart, geoZoomRef.current / GEO_ZOOM_STEP);
  }, [applyChartGeoView]);

  const handleGeoRoam = useCallback(() => {
    const chart = chartRef.current;
    if (!isChartAlive(chart)) return;

    const currentZoom = readGeoZoomFromChart(chart);
    const currentCenter = readGeoCenterFromChart(chart);
    const clampedZoom = clampGeoZoom(currentZoom);
    const clampedCenter = clampGeoCenter(currentCenter, clampedZoom);

    geoCenterRef.current = clampedCenter;
    geoZoomRef.current = clampedZoom;

    if (isGeoViewChanged(currentZoom, currentCenter, clampedZoom, clampedCenter)) {
      chart.setOption({
        geo: getGeoConfig(clampedZoom, clampedCenter),
      });
    }

    scheduleGeoZoomUiSync();
  }, [scheduleGeoZoomUiSync]);

  const scheduleGeoRoam = useCallback(() => {
    if (geoRoamFrameRef.current !== null) return;

    geoRoamFrameRef.current = requestAnimationFrame(() => {
      geoRoamFrameRef.current = null;
      handleGeoRoam();
    });
  }, [handleGeoRoam]);

  const updateLocationTooltip = useCallback(
    (chart: echarts.ECharts, pointerX: number, pointerY: number) => {
      const nextTooltip = findHoveredLocation(chart, safeLocations, pointerX, pointerY);
      setTooltip((prev) => {
        if (!prev && !nextTooltip) return prev;
        if (
          prev &&
          nextTooltip &&
          prev.name === nextTooltip.name &&
          prev.value === nextTooltip.value &&
          Math.abs(prev.x - nextTooltip.x) < 0.5 &&
          Math.abs(prev.y - nextTooltip.y) < 0.5
        ) {
          return prev;
        }
        return nextTooltip;
      });
    },
    [safeLocations],
  );

  const bindCanvasPan = useCallback(
    (chart: echarts.ECharts) => {
      const zr = chart.getZr();

      const onMouseDown = (params: ZrPointerEvent) => {
        if (params.event.button !== 0) return;

        panPendingRef.current = {
          pointerX: params.offsetX,
          pointerY: params.offsetY,
          center: readGeoCenterFromChart(chart),
          zoom: readGeoZoomFromChart(chart),
        };
      };

      const onMouseMove = (params: ZrPointerEvent) => {
        if (!isChartAlive(chart)) return;

        if (!geoDragRef.current && panPendingRef.current) {
          const pending = panPendingRef.current;
          const distance = Math.hypot(
            params.offsetX - pending.pointerX,
            params.offsetY - pending.pointerY,
          );

          if (distance < PAN_DRAG_THRESHOLD_PX) return;

          geoDragRef.current = pending;
          chart.dispatchAction({ type: 'hideTip' });
          setTooltip(null);
          setChartDraggingClass(chart, true);
        }

        const drag = geoDragRef.current;
        if (!drag) {
          updateLocationTooltip(chart, params.offsetX, params.offsetY);
          return;
        }

        const startCoord = convertPixelToGeo(chart, drag.pointerX, drag.pointerY);
        const nowCoord = convertPixelToGeo(chart, params.offsetX, params.offsetY);
        if (!startCoord || !nowCoord) return;

        const nextCenter = clampGeoCenter(
          [
            drag.center[0] - (nowCoord[0] - startCoord[0]),
            drag.center[1] - (nowCoord[1] - startCoord[1]),
          ],
          drag.zoom,
        );

        if (
          Math.abs(nextCenter[0] - geoCenterRef.current[0]) < 0.0001 &&
          Math.abs(nextCenter[1] - geoCenterRef.current[1]) < 0.0001
        ) {
          return;
        }

        geoCenterRef.current = nextCenter;
        chart.setOption({ geo: getGeoConfig(drag.zoom, nextCenter) }, { lazyUpdate: true });
      };

      const onMouseUp = () => {
        panPendingRef.current = null;
        geoDragRef.current = null;
        if (isChartAlive(chart)) {
          setChartDraggingClass(chart, false);
        }
      };

      const onGlobalOut = () => {
        onMouseUp();
        setTooltip(null);
      };

      zr.on('mousedown', onMouseDown);
      zr.on('mousemove', onMouseMove);
      zr.on('mouseup', onMouseUp);
      zr.on('globalout', onGlobalOut);

      return () => {
        zr.off('mousedown', onMouseDown);
        zr.off('mousemove', onMouseMove);
        zr.off('mouseup', onMouseUp);
        zr.off('globalout', onGlobalOut);
      };
    },
    [updateLocationTooltip],
  );

  const handleChartReady = useCallback(
    (chart: echarts.ECharts) => {
      unbindCanvasPanRef.current?.();
      chartRef.current = chart;
      geoZoomRef.current = readGeoZoomFromChart(chart);
      geoCenterRef.current = readGeoCenterFromChart(chart);
      setGeoZoomUi(geoZoomRef.current);

      chart.off('georoam', scheduleGeoRoam);
      chart.on('georoam', scheduleGeoRoam);

      unbindCanvasPanRef.current = bindCanvasPan(chart);
    },
    [bindCanvasPan, scheduleGeoRoam],
  );

  useEffect(() => {
    return () => {
      if (geoRoamFrameRef.current !== null) {
        cancelAnimationFrame(geoRoamFrameRef.current);
      }
      if (geoRoamSyncTimeoutRef.current) {
        clearTimeout(geoRoamSyncTimeoutRef.current);
      }
      unbindCanvasPanRef.current?.();
      unbindCanvasPanRef.current = null;
      geoDragRef.current = null;
      panPendingRef.current = null;
      setTooltip(null);
      const chart = chartRef.current;
      if (isChartAlive(chart)) {
        chart.off('georoam', scheduleGeoRoam);
      }
      chartRef.current = null;
    };
  }, [scheduleGeoRoam]);

  const legendMaxLabel = `${valueMax}+`;

  return (
    <Card>
      <CardHeader title={title} />

      <ChartWrap>
        {mapReady && (
          <ReactECharts
            option={option}
            lazyUpdate
            style={{ width: '100%', height: '100%' }}
            opts={{ renderer: 'canvas' }}
            onChartReady={handleChartReady}
          />
        )}

        {tooltip && (
          <LocationTooltip style={{ left: tooltip.x, top: tooltip.y }}>
            <Typography variant="textXS" weight="semibold" color="--gray-90" style={{ margin: 0 }}>
              {tooltip.name ? `${tooltip.name}: ${tooltip.value}` : tooltip.value}
            </Typography>
          </LocationTooltip>
        )}

        <LegendOverlay>
          <Typography variant="textXS" weight="semibold" color="--gray-80" style={{ margin: 0 }}>
            {legendTitle}
          </Typography>
          <GradientBar />
          <GradientLabels>
            <Typography variant="textXS" weight="semibold" color="--gray-80" style={{ margin: 0 }}>
              1
            </Typography>
            <Typography
              variant="textXS"
              weight="semibold"
              color="--gray-80"
              style={{ margin: 0, textAlign: 'right' }}
            >
              {legendMaxLabel}
            </Typography>
          </GradientLabels>
        </LegendOverlay>

        <ZoomControls>
          <ZoomButton
            type="button"
            aria-label="Zoom in"
            onClick={zoomIn}
            disabled={!mapReady || geoZoomUi >= GEO_MAX_ZOOM}
          >
            +
          </ZoomButton>
          <ZoomButton
            type="button"
            aria-label="Zoom out"
            onClick={zoomOut}
            disabled={!mapReady || geoZoomUi <= GEO_MIN_ZOOM}
          >
            −
          </ZoomButton>
        </ZoomControls>
      </ChartWrap>
    </Card>
  );
};
